import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { ORBIT_UNLOCK_THRESHOLDS, ORBIT_ORDER } from '../data/orbits';

// Free orbits available to all users (with XP unlock)
const FREE_ORBITS = ['orbital_elements', 'leo', 'polar', 'geo', 'sso'];
// Premium orbits require payment
const PREMIUM_ORBITS = ['meo', 'heo', 'molniya', 'tundra', 'graveyard', 'hohmann', 'lagrange'];
const PREMIUM_FEATURES = ['tle_parser', 'camera_lock', 'editable_data'];

// XP thresholds for free orbits
const FREE_UNLOCK_THRESHOLDS = {
  orbital_elements: 0,
  leo: 0,
  polar: 50,
  geo: 100,
  sso: 200,
};

const STORAGE_KEY = 'orbitops_progress';

const DEFAULT_PROGRESS = {
  totalPoints: 0,
  unlockedOrbits: ['orbital_elements', 'leo'],
  completedLessons: [],
  quizScores: {},
  achievements: [],
  isPremium: false,
  sessionId: null,
  lastActive: new Date().toISOString(),
};

// Helper to read from localStorage
function getStoredProgress() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (!parsed.sessionId) {
        parsed.sessionId = 'session_' + Math.random().toString(36).substring(2, 15);
      }
      return { ...DEFAULT_PROGRESS, ...parsed };
    }
  } catch (e) {
    console.error('Failed to read progress:', e);
  }
  return { 
    ...DEFAULT_PROGRESS, 
    sessionId: 'session_' + Math.random().toString(36).substring(2, 15) 
  };
}

// Helper to save to localStorage
function saveProgress(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    // Dispatch event so other tabs/components can sync
    window.dispatchEvent(new Event('orbitops-progress-update'));
  } catch (e) {
    console.error('Failed to save progress:', e);
  }
}

export function useProgress() {
  // Initialize state from localStorage
  const [progress, setProgress] = useState(getStoredProgress);
  const savingRef = { current: false };

  // Listen for storage changes (from other tabs or components)
  useEffect(() => {
    const handleStorageChange = () => {
      // Skip if this component just wrote to storage
      if (savingRef.current) return;
      const stored = getStoredProgress();
      setProgress(stored);
    };

    // Listen for our custom event
    window.addEventListener('orbitops-progress-update', handleStorageChange);
    // Also listen for storage events from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) handleStorageChange();
    });

    return () => {
      window.removeEventListener('orbitops-progress-update', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Save to localStorage whenever progress changes
  useEffect(() => {
    savingRef.current = true;
    saveProgress(progress);
    // Allow other components to react after a tick
    requestAnimationFrame(() => { savingRef.current = false; });
  }, [progress]);

  const addPoints = (pts) => {
    setProgress(p => {
      const newTotal = p.totalPoints + pts;
      
      // Calculate newly unlocked FREE orbits based on XP
      const xpUnlocked = Object.entries(FREE_UNLOCK_THRESHOLDS)
        .filter(([_, threshold]) => newTotal >= threshold)
        .map(([id]) => id);
      
      // If premium, also include all premium orbits
      const premiumUnlocked = p.isPremium ? PREMIUM_ORBITS : [];
      
      const newUnlocked = [...new Set([...xpUnlocked, ...premiumUnlocked])];
      
      const newAchievements = [...p.achievements];
      if (newTotal >= 100 && !newAchievements.includes('first_100')) newAchievements.push('first_100');
      if (newTotal >= 500 && !newAchievements.includes('halfway')) newAchievements.push('halfway');
      if (newTotal >= 1000 && !newAchievements.includes('master')) newAchievements.push('master');
      
      return { ...p, totalPoints: newTotal, unlockedOrbits: newUnlocked, achievements: newAchievements };
    });
  };

  const unlockOrbit = (orbitId) => {
    setProgress(p => ({
      ...p,
      unlockedOrbits: [...new Set([...p.unlockedOrbits, orbitId])],
    }));
  };

  const markLessonComplete = (orbitId) => {
    setProgress(p => {
      if (p.completedLessons.includes(orbitId)) return p;
      return {
        ...p,
        completedLessons: [...p.completedLessons, orbitId],
      };
    });
  };

  const saveQuizScore = (orbitId, score, maxScore) => {
    setProgress(p => {
      const existing = p.quizScores[orbitId] || 0;
      if (score <= existing) return p;
      return {
        ...p,
        quizScores: { ...p.quizScores, [orbitId]: score },
      };
    });
  };

  const setPremium = (isPremium) => {
    setProgress(p => {
      let newProgress;
      if (isPremium) {
        // Unlock all premium orbits
        const allUnlocked = [...new Set([...p.unlockedOrbits, ...PREMIUM_ORBITS])];
        newProgress = { ...p, isPremium: true, unlockedOrbits: allUnlocked };
      } else {
        newProgress = { ...p, isPremium: false };
      }
      // Save to localStorage IMMEDIATELY (don't wait for useEffect)
      try {
        localStorage.setItem('orbitops_progress', JSON.stringify(newProgress));
      } catch (e) {
        console.error('Failed to save premium status:', e);
      }
      return newProgress;
    });
  };

  const isOrbitUnlocked = (orbitId) => {
    // If premium, all orbits are unlocked
    if (progress.isPremium) {
      return true;
    }
    // Free orbits: check XP threshold
    if (FREE_ORBITS.includes(orbitId)) {
      const threshold = FREE_UNLOCK_THRESHOLDS[orbitId] || 0;
      return progress.totalPoints >= threshold;
    }
    return false;
  };

  const isFeatureUnlocked = (featureId) => {
    return progress.isPremium;
  };

  const isLessonComplete = (orbitId) => progress.completedLessons.includes(orbitId);
  const getQuizScore = (orbitId) => progress.quizScores[orbitId] || 0;
  const getCompletionPercent = () => {
    const total = ORBIT_ORDER.length;
    const done = progress.completedLessons.length;
    return Math.round((done / total) * 100);
  };

  const resetProgress = () => setProgress({
    ...DEFAULT_PROGRESS,
    sessionId: progress.sessionId  // Keep same session
  });

  // Refresh progress from localStorage (useful after external changes)
  const refreshProgress = useCallback(() => {
    setProgress(getStoredProgress());
  }, []);

  return {
    progress,
    addPoints,
    unlockOrbit,
    markLessonComplete,
    saveQuizScore,
    isOrbitUnlocked,
    isFeatureUnlocked,
    isLessonComplete,
    getQuizScore,
    getCompletionPercent,
    resetProgress,
    setPremium,
    refreshProgress,
    FREE_ORBITS,
    PREMIUM_ORBITS,
    PREMIUM_FEATURES,
  };
}
